import { NuevaPassword, useAuth } from '@eg/compartido'
import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { es } from '../../content/es'
import { Acceso } from './Acceso'
import { Mfa } from './Mfa'

/**
 * ¿Se exige verificación en dos pasos al personal? Lo decide el interruptor `mfa_personal` de la base (hoy
 * apagado: basta usuario y contraseña). Se lee sin sesión porque los interruptores son públicos.
 */
export function useMfaRequerida() {
  const { cliente } = useAuth()
  return useQuery({
    queryKey: ['mfa-requerida'],
    enabled: cliente !== null,
    staleTime: 60_000,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await cliente!.from('flags').select('activo').eq('clave', 'mfa_personal').maybeSingle()
      if (error) throw error
      return Boolean(data?.activo)
    },
  })
}

function Esqueleto({ texto }: { texto: string }) {
  return (
    <div role="status" aria-live="polite">
      <p className="principal__intro">{texto}</p>
      <div className="esqueleto" />
    </div>
  )
}

function Pantalla({ titulo, intro, children }: { titulo: string; intro?: string; children: ReactNode }) {
  return (
    <>
      <h1>{titulo}</h1>
      {intro && <p className="principal__intro prosa">{intro}</p>}
      <div className="principal__cuerpo">{children}</div>
    </>
  )
}

/**
 * Todo el portal vive detrás de esta puerta: sesión y una fila activa en `personal` (más la verificación en dos
 * pasos solo si el interruptor mfa_personal está encendido). La base lo exige igual con RLS; esto solo evita
 * mostrar pantallas vacías o con errores.
 */
export function Puerta({ children }: { children: ReactNode }) {
  const { cliente, cargando, sesion, nivelActual, recuperando, cerrarSesion } = useAuth()
  const mfa = useMfaRequerida()
  const uid = sesion?.user.id
  const faltaMfa = Boolean(mfa.data) && nivelActual !== 'aal2'
  const listo = Boolean(cliente && uid && mfa.isSuccess && !faltaMfa && !recuperando)
  const personal = useQuery({
    queryKey: ['personal', uid],
    enabled: listo,
    queryFn: async () => {
      const { data, error } = await cliente!.from('personal').select('nombre, roles, activo').eq('user_id', uid!).maybeSingle()
      if (error) throw error
      return data
    },
  })

  if (cliente === null) {
    return (
      <Pantalla titulo={es.acceso.titulo}>
        <div className="aviso-vacio aviso-vacio--error" role="alert">
          <h2>{es.puerta.sinConexionTitulo}</h2>
          <p>{es.puerta.sinConexionTexto}</p>
        </div>
      </Pantalla>
    )
  }
  if (cargando || (sesion && mfa.isPending)) {
    return (
      <Pantalla titulo={es.acceso.titulo}>
        <Esqueleto texto={es.acceso.cargando} />
      </Pantalla>
    )
  }
  if (recuperando && sesion) {
    return (
      <Pantalla titulo={es.acceso.nuevaTitulo} intro={es.acceso.nuevaIntro}>
        <NuevaPassword />
      </Pantalla>
    )
  }
  if (!sesion) {
    return (
      <Pantalla titulo={es.acceso.titulo} intro={es.acceso.intro}>
        <Acceso />
      </Pantalla>
    )
  }
  if (faltaMfa) {
    return (
      <Pantalla titulo={es.acceso.titulo}>
        <Mfa />
      </Pantalla>
    )
  }
  if (personal.isPending) {
    return (
      <Pantalla titulo={es.acceso.titulo}>
        <Esqueleto texto={es.puerta.verificando} />
      </Pantalla>
    )
  }
  if (personal.isError || !personal.data?.activo) {
    return (
      <Pantalla titulo={es.puerta.sinAccesoTitulo}>
        <div className="aviso-vacio aviso-vacio--error" role="alert">
          <p>{es.puerta.sinAccesoTexto}</p>
          <p className="accion">
            <button type="button" className="boton boton--secundario" onClick={() => void cerrarSesion()}>
              {es.cerrarSesion}
            </button>
          </p>
        </div>
      </Pantalla>
    )
  }
  return <>{children}</>
}
